import * as React from 'react';
import { FC, useRef } from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { useIntl } from 'react-intl';
import { Dimensions, StyleSheet, ViewStyle } from 'react-native';
import { View } from 'react-native';

import { ActionButtonVariants } from 'components/action-button';
import { Helmet } from 'components/helmet';
import { LayoutContainer } from 'components/layout/styled';
import { CustomScrollViewProps } from 'components/onboarding-multi-step/types';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { routes } from 'navigation/routing';
import { StyledButton } from 'pages/loyalty/loyalty-widgets/loyalty-button-array-widget/loyalty-button-array-widget.styled';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { primitive } from 'styles/constants/primitives';

import { ShortCodeOfferStatusPoller } from '../components/short-code-offer-status-poller';
import { ShortCodePaymentResultPoller } from '../components/short-code-payment-result-poller';
import { TermsText } from '../loyalty-in-restaurant-redemption.styled';
import { InRestaurantRedemptionRewardList } from '../reward-redemption-list';
import { useLoyaltyMyCode } from '../use-loyalty-my-code';

import { LoyaltyQrCodeLoyaltyIdWidgetRedesign } from './loyalty-qr-code-loyalty-id-widget-redesign';
import { LoyaltyQuickPayWidgetRedesign } from './loyalty-quick-pay-widget-redesign';
import LoyaltyShortCodeWidgetRedesign from './loyalty-short-code-widget-redesign';

const screenHeight = Dimensions.get('window').height;

const LoyaltyRedemptionRedesign: FC<React.PropsWithChildren> = () => {
  const isMobile = useIsMobileBreakpoint();
  const { formatMessage } = useIntl();
  const { data, loading } = useLoyaltyMyCode();
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);

  const scrollViewRef = useRef<CustomScrollViewProps | null>(null);

  if (loading) {
    return null;
  }

  // Offer message
  const addOfferMessage = formatMessage({ id: 'addOffer', defaultMessage: 'Add Offer' });
  const addOffersMessage = formatMessage({ id: 'addOffers' });

  // Reward message
  const addRewardMessage = formatMessage({ id: 'addReward', defaultMessage: 'Add Reward' });
  const addRewardsMessage = formatMessage({ id: 'addRewards' });

  return (
    <>
      <LayoutContainer isFullContainer={isMobile}>
        <ScrollView ref={scrollViewRef}>
          <Helmet title={formatMessage({ id: 'redeemInRestaurant' })} />
          <View style={styles.contentContainer}>
            <View style={styles.widgetContainer}>
              <LoyaltyShortCodeWidgetRedesign />
              <LoyaltyQrCodeLoyaltyIdWidgetRedesign />
              <View style={styles.widget}>
                <LoyaltyQuickPayWidgetRedesign />
                <View style={styles.row}>
                  <StyledButton to={routes.loyaltyOfferList} variant={ActionButtonVariants.OUTLINE}>
                    {enableRewardUnstacking ? addOfferMessage : addOffersMessage}
                  </StyledButton>
                  <StyledButton
                    to={routes.loyaltyRewardsList}
                    variant={ActionButtonVariants.OUTLINE}
                  >
                    {enableRewardUnstacking ? addRewardMessage : addRewardsMessage}
                  </StyledButton>
                </View>
                <InRestaurantRedemptionRewardList loyaltyEmptyCartTileWidget={null} />
              </View>
            </View>
            {!loading && data?.myCodeLegalTerms?.locale && (
              <View style={styles.termsContainer}>
                <TermsText
                  textProps={{
                    textAlign: 'center',
                    fontSize: '2xs',
                    lineHeight: 'lg',
                    marginLeft: '$10',
                    marginRight: '$10',
                    color: primitive.bk.$mayoEggWhite,
                  }}
                  content={data?.myCodeLegalTerms?.locale}
                />
              </View>
            )}
          </View>
        </ScrollView>
      </LayoutContainer>
      <ShortCodeOfferStatusPoller />
      <ShortCodePaymentResultPoller />
    </>
  );
};

const widgetContainerMinHeight = 540;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: spacing.$4,
    marginBottom: spacing.$4,
    marginLeft: spacing.$2,
    marginRight: spacing.$2,
  },
  widgetContainer: {
    minHeight: widgetContainerMinHeight,
  },
  contentContainer: {
    paddingTop: spacing.$3,
    paddingBottom: spacing.$3,
    paddingLeft: spacing.$4,
    paddingRight: spacing.$4,
    backgroundColor: primitive.bk.$bbqBrown,
    minHeight: screenHeight,
  } as ViewStyle,
  widget: {
    backgroundColor: 'white',
    marginBottom: spacing.$3,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  termsContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: spacing.$3,
    paddingBottom: spacing.$3,
  } as ViewStyle,
});

export default LoyaltyRedemptionRedesign;
